import {
    logDatapoint as logDatapointCore,
    logDatapointWithObservation,
} from 'owa-analytics-core/lib/loggingFunctions/logDatapoint';
import type { LogDatapointParams } from 'owa-analytics-types';
import { getAnalyticsAddon } from 'owa-analytics-addons';
import { getThreadName } from 'owa-thread-config';
import { queueOrRun } from 'owa-analytics-utils';
import { alertWorkerLoggingError } from 'owa-analytics-utils/lib/alertWorkerLoggingError';
import { getLogDatapointObserver } from 'owa-analytics-utils/lib/logDatapointObservation';
import { logToTracingPanel } from '../../utils/logToTracingPanel';
import { getAnalyticsFlightsAndAppSettings } from 'owa-analytics-core/lib/settings/getAnalyticsFlightsAndAppSettings';
import { isRunningOnWorker } from 'owa-config';
import { enrichDatapointWithAppVisibility, resolveDatapointTimeGate } from 'owa-analytics-core';
import { trace } from 'owa-trace';
import { runDatapointTimeGateDiagnostics } from 'owa-analytics-core/lib/utils/datapointTimeGate';
import { cacheDatapointForDiagnostics } from '../../utils/cacheDatapointForDiagnostics';

export function logDatapoint(...args: LogDatapointParams): void {
    const [datapoint, , observation] = args;
    if (observation && !getLogDatapointObserver(observation)?.isActive()) {
        return;
    }
    if (resolveDatapointTimeGate(datapoint.getOptions() ?? {}) === undefined) {
        logToTracingPanel(datapoint);
    }
    enrichDatapointWithAppVisibility(datapoint);

    queueOrRun<LogDatapointParams>('logDatapoint', logDatapointFunction, ...args);
}

export function logDatapointFunction(...args: LogDatapointParams): void {
    const [datapoint, overrideEventType, observation] = args;
    if (observation && !getLogDatapointObserver(observation)?.isActive()) {
        return;
    }
    if (resolveDatapointTimeGate(datapoint.getOptions() ?? {}) !== undefined) {
        const onTimeGateAdmitted = () => {
            runDatapointTimeGateDiagnostics(
                () => cacheDatapointForDiagnostics(datapoint, true),
                () => logToTracingPanel(datapoint),
                () => logDiagnostics(datapoint)
            );
        };
        if (observation) {
            dispatchObservedDatapoint(
                datapoint,
                overrideEventType,
                observation,
                onTimeGateAdmitted
            );
        } else {
            void logDatapointCore(
                datapoint,
                overrideEventType,
                undefined /* batchId */,
                undefined /* observer */,
                onTimeGateAdmitted
            ).catch(alertWorkerLoggingError);
        }
        return;
    }
    if (getThreadName() === 'MAIN_THREAD') {
        getAnalyticsAddon('LogInWorkerOrMainThread')?.executeWhenReady(
            observation ? dispatchObservedDatapoint : logDatapointCore,
            'logDatapoint',
            ...args
        );
    } else if (observation) {
        dispatchObservedDatapoint(datapoint, overrideEventType, observation);
    } else {
        logDatapointCore(datapoint, overrideEventType);
    }

    logDiagnostics(datapoint);
}

function logDiagnostics(datapoint: LogDatapointParams[0]): void {
    if (!isRunningOnWorker() && getAnalyticsFlightsAndAppSettings()?.devToolsEnabled) {
        getAnalyticsAddon('LogUnifiedTelemetryDiagnostics')?.executeWhenReady(datapoint);
    }
}

function dispatchObservedDatapoint(
    datapoint: LogDatapointParams[0],
    overrideEventType: LogDatapointParams[1],
    observation: LogDatapointParams[2],
    onTimeGateAdmitted?: () => void
): void {
    if (!observation) {
        trace.warn('Config-state dispatch is missing its observation.', 'analytics');
        return;
    }
    void logDatapointWithObservation(
        datapoint,
        overrideEventType,
        observation,
        undefined /* batchId */,
        onTimeGateAdmitted
    ).catch(() => {
        trace.warn('Config-state core dispatch failed.', 'analytics');
    });
}
